import { useMemo, useState } from "react";

import { Models } from "@/api/types";
import { convertToFullName } from "@/lib/utils";
import { formatPhoneNumber } from "@/toolbox/PhoneNumber";

import { GiftCardPreviewAppOnly } from "../GiftCardPreview/GiftCardPreview";
import {
  GiftCardFormData,
  GiftCardPurchaseForm,
} from "../GiftCardPurchaseForm/GiftCardPurchaseForm";
import { DiscountMap } from "../giftCards.types";
import { GiftCardShareModal } from "../GiftCardShareModal/GiftCardShareModal";

type GiftCardsShopContainerProps = {
  giftCards: Models<"DisplayGiftCardListing">[];
  platform: Models<"PlatformModel">;
  currentUser: Models<"UserModel1">;
  availablePromotions: DiscountMap;
};

export const GiftCardsShopContainer = ({
  giftCards,
  currentUser,
  platform,
  availablePromotions,
}: GiftCardsShopContainerProps) => {
  const [showShareModal, setShowShareModal] = useState(false);

  const defaultValues: GiftCardFormData = useMemo(
    () => ({
      recipientType: {
        value: "someone-else",
        label: "Someone Else",
      },
      giftCardListingId: giftCards[0]?.id,
      amountInCents: giftCards[0]?.giftCards[0]?.baseCostInCents,
      senderFirstName: currentUser?.firstName || "",
      senderLastName: currentUser?.lastName || "",
      senderPhoneNumber: formatPhoneNumber(currentUser.phoneNumber),
      senderEmail: currentUser?.email || "",
      recipientFirstName: "",
      recipientLastName: "",
      recipientPhoneNumber: "",
      recipientEmail: "",
      message: "",
      deliveryType: {
        value: "immediate",
        label: "Immediate",
      },
      deliveryDate: undefined,
      deliveryTime: undefined,
    }),
    []
  );

  const [previewData, setPreviewData] =
    useState<GiftCardFormData>(defaultValues);

  const handleFormChange = (data: GiftCardFormData) => {
    setPreviewData(data);
  };

  const selectedGiftCardListing = giftCards.find(
    (giftCard) => giftCard.id === previewData.giftCardListingId
  );

  const imageUri = selectedGiftCardListing?.imageUri;

  const giftCardItem = selectedGiftCardListing?.giftCards.find(
    (giftCard) => giftCard.baseCostInCents === previewData.amountInCents
  );

  const giftCardValue = giftCardItem?.baseCostInCents;

  const handleShareClick = () => {
    setShowShareModal(true);
  };

  return (
    <div className="GiftCardsShopContainer bg-gray-50">
      <GiftCardPreviewAppOnly
        preview={{
          image: imageUri ?? "",
          valueInCents: giftCardValue ?? 0,
        }}
        recipientName={convertToFullName(
          previewData.recipientFirstName,
          previewData.recipientLastName
        )}
        senderName={convertToFullName(
          previewData.senderFirstName,
          previewData.senderLastName
        )}
        platformLogo={platform?.mobileAppIconUrlMedium ?? ""}
        isSomeoneElse={previewData.recipientType.value === "someone-else"}
        onShareClick={handleShareClick}
      />
      <GiftCardPurchaseForm
        defaultValues={defaultValues}
        giftCards={giftCards}
        onChange={handleFormChange}
        linkToCart="/cart"
        disableSenderFields={true}
        purchaseSource="App"
        availablePromotions={availablePromotions}
        platform={platform}
        isAuthenticated={true}
      >
        <GiftCardPreviewAppOnly
          preview={{
            image: imageUri ?? "",
            valueInCents: giftCardValue ?? 0,
          }}
          recipientName={convertToFullName(
            previewData.recipientFirstName,
            previewData.recipientLastName
          )}
          senderName={convertToFullName(
            previewData.senderFirstName,
            previewData.senderLastName
          )}
          platformLogo={platform?.mobileAppIconUrlMedium ?? ""}
          isSomeoneElse={previewData.recipientType.value === "someone-else"}
          onShareClick={handleShareClick}
        />
      </GiftCardPurchaseForm>

      <GiftCardShareModal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        giftCards={giftCards}
        platformLogo={platform?.mobileAppIconUrlMedium ?? ""}
        locationId={currentUser?.platformLocation?.id ?? ""}
        selectedGiftCard={{
          listingId: previewData.giftCardListingId,
          itemId: giftCardItem?.packageId ?? "",
        }}
      />
    </div>
  );
};
